import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Row, Col, Form, Button, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaSort, FaSortUp, FaSortDown, FaSearch, FaEraser, FaInfoCircle, FaEye } from 'react-icons/fa';

import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { intToHexColor } from '~/Utils/Helpers';
import * as api from '~/Services/Service';
import ModalDetalhamento from './ModalDetalhamento';
import './styles.css';

const STATUS_OPTIONS = [
  { value: null, label: 'Todos' },
  { value: 'D', label: 'Disponível' },
  { value: 'P', label: 'Pré-Venda' },
];

const STATUS_CONFIG = {
  D: { variant: 'success', label: 'Disponível' },
  P: { variant: 'info', label: 'Pré-Venda' },
};

const formatarMonetario = valor => {
  if (valor === null || valor === undefined) return '-';
  return Number(valor).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function IntencoesVendas() {
  const [geOptions, setGeOptions] = useState([]);
  const [geSelecionado, setGeSelecionado] = useState(null);
  const [allEmpreendimentos, setAllEmpreendimentos] = useState([]);
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(null);
  const [filtroImovel, setFiltroImovel] = useState('');
  const [filtroPreVenda, setFiltroPreVenda] = useState('');
  const [filtroCorretor, setFiltroCorretor] = useState('');
  const [filtroStatus, setFiltroStatus] = useState(null);
  const [intencoes, setIntencoes] = useState([]);
  const [intencaoSelecionada, setIntencaoSelecionada] = useState(null);
  const [prestadores, setPrestadores] = useState([]);
  const [textoComissao, setTextoComissao] = useState([]);
  const [ordenacao, setOrdenacao] = useState({ campo: 'codigo_ativo', direcao: 'asc' });
  const [loading, setLoading] = useState(false);
  const [loadingPrestadores, setLoadingPrestadores] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [detalhamento, setDetalhamento] = useState(null);
  const [prestadorDetalhe, setPrestadorDetalhe] = useState(null);

  // Mount: carregar GEs e empreendimentos
  useEffect(() => {
    carregarGes();
    carregarEmpreendimentos();
  }, []);

  // Ao trocar GE: limpar filtros incompativeis e buscar
  useEffect(() => {
    if (geSelecionado) {
      // Preservar empreendimento se pertence ao GE selecionado
      let empValido = null;
      if (empreendimentoSelecionado) {
        const emp = allEmpreendimentos.find(
          e => e.codigo_familia === empreendimentoSelecionado.value
        );
        if (emp && emp.codigo_grupo_empresa === geSelecionado.value) {
          empValido = empreendimentoSelecionado.value;
        } else {
          setEmpreendimentoSelecionado(null);
        }
      }
      setIntencaoSelecionada(null);
      setPrestadores([]);
      setTextoComissao([]);
      buscarIntencoes(geSelecionado.value, empValido, null, null, null, filtroStatus);
    } else {
      setIntencoes([]);
      setIntencaoSelecionada(null);
      setPrestadores([]);
      setTextoComissao([]);
    }
  }, [geSelecionado]);

  // Ao selecionar intencao: carregar prestadores
  useEffect(() => {
    if (intencaoSelecionada) {
      if (!intencaoSelecionada.pre_venda) {
        setPrestadores([]);
        setTextoComissao([]);
        return;
      }
      carregarPrestadores(intencaoSelecionada);
    } else {
      setPrestadores([]);
      setTextoComissao([]);
    }
  }, [intencaoSelecionada]);

  const carregarGes = async () => {
    try {
      const res = await api.listarTodosGruposEmpresa();
      const opts = (res || []).map(ge => ({
        value: ge.codigo,
        label: `${ge.codigo} - ${ge.apelido}`,
      }));
      setGeOptions(opts);
      if (opts.length === 1) setGeSelecionado(opts[0]);
    } catch {
      toast.error('Erro ao carregar grupos empresa.');
    }
  };

  const carregarEmpreendimentos = async () => {
    try {
      const res = await api.listarEmpreendimentosIntencaoVenda();
      setAllEmpreendimentos(res || []);
    } catch {
      setAllEmpreendimentos([]);
    }
  };

  // Derivar opcoes do select de empreendimento a partir do GE selecionado
  const empreendimentoOptions = geSelecionado
    ? allEmpreendimentos
        .filter(e => e.codigo_grupo_empresa === geSelecionado.value)
        .map(e => ({ value: e.codigo_familia, label: e.nome_familia }))
    : allEmpreendimentos.map(e => ({
        value: e.codigo_familia,
        label: `${e.nome_familia} (${e.codigo_grupo_empresa} - ${e.apelido_grupo_empresa})`,
        ge: e.codigo_grupo_empresa,
      }));

  // Selecao bidirecional: empreendimento -> auto-seleciona GE
  const handleEmpreendimentoChange = option => {
    setEmpreendimentoSelecionado(option);
    if (option && !geSelecionado) {
      const emp = allEmpreendimentos.find(e => e.codigo_familia === option.value);
      if (emp) {
        const geOpt = geOptions.find(g => g.value === emp.codigo_grupo_empresa);
        if (geOpt) setGeSelecionado(geOpt);
      }
    }
  };

  const buscarIntencoes = async (ge, empreendimento, imovel, prevenda, corretor, status) => {
    if (!ge) {
      toast.error('Selecione um Grupo Empresa.');
      return;
    }
    if (corretor && isNaN(Number(corretor))) {
      toast.error('Código inválido no campo Corretor.');
      return;
    }
    setLoading(true);
    setIntencaoSelecionada(null);
    setPrestadores([]);
    setTextoComissao([]);
    try {
      const res = await api.listarIntencoesVendas(
        ge,
        empreendimento || undefined,
        imovel || undefined,
        prevenda || undefined,
        corretor || undefined,
        status || undefined
      );
      setIntencoes(res || []);
      if (!res || res.length === 0) {
        toast.info('Nenhum registro encontrado.');
      }
    } catch {
      toast.error('Erro ao buscar intenções de vendas.');
    } finally {
      setLoading(false);
    }
  };

  const carregarPrestadores = async intencao => {
    if (!intencao.pre_venda) return;
    setLoadingPrestadores(true);
    try {
      const res = await api.listarPrestadoresIntencaoVenda(
        intencao.pre_venda,
        intencao.codigo_ativo,
        intencao.codigo_proposta
      );
      const lista = res || [];
      setPrestadores(lista);
      const linhasObs = lista
        .filter(p => p.obs)
        .map(p => `SEQ: ${p.seq} - ${p.obs}`);
      setTextoComissao(linhasObs);
    } catch {
      toast.error('Erro ao carregar prestadores.');
      setPrestadores([]);
      setTextoComissao([]);
    } finally {
      setLoadingPrestadores(false);
    }
  };

  const handleBuscar = () => {
    if (!geSelecionado) {
      toast.error('Selecione um Grupo Empresa.');
      return;
    }
    buscarIntencoes(
      geSelecionado.value,
      empreendimentoSelecionado ? empreendimentoSelecionado.value : null,
      filtroImovel,
      filtroPreVenda,
      filtroCorretor,
      filtroStatus
    );
  };

  const handleLimpar = () => {
    setEmpreendimentoSelecionado(null);
    setFiltroImovel('');
    setFiltroPreVenda('');
    setFiltroCorretor('');
    setFiltroStatus(null);
    setIntencaoSelecionada(null);
    setPrestadores([]);
    setTextoComissao([]);
    if (geSelecionado) {
      buscarIntencoes(geSelecionado.value, null, null, null, null, null);
    }
  };

  const handleDetalhar = async prestador => {
    if (!intencaoSelecionada) return;
    try {
      const res = await api.detalharIntencaoVenda(
        intencaoSelecionada.pre_venda,
        prestador.codigo_conta,
        prestador.codigo_tipo
      );
      if (!res) {
        toast.info('Nenhum registro encontrado.');
        return;
      }
      setDetalhamento(res);
      setPrestadorDetalhe(prestador);
      setShowModal(true);
    } catch {
      toast.error('Erro ao carregar detalhamento.');
    }
  };

  const handleOrdenacao = campo => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const iconeOrdenacao = campo => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const intencoesOrdenadas = [...intencoes].sort((a, b) => {
    const valA = a[ordenacao.campo];
    const valB = b[ordenacao.campo];
    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;
    const cmp = String(valA).localeCompare(String(valB), undefined, { numeric: true });
    return ordenacao.direcao === 'asc' ? cmp : -cmp;
  });

  const renderStatusBadge = (status, fullLabel) => {
    const cfg = STATUS_CONFIG[status] || { variant: 'secondary', label: status || '-' };
    return <Badge variant={cfg.variant}>{fullLabel ? cfg.label : (status || '-')}</Badge>;
  };

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Intenções de Vendas" />

      {/* ===== FILTROS ===== */}
      <Card className="mb-3 shadow-sm">
        <Card.Header
          className="d-flex align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>Filtros</strong>
        </Card.Header>
        <Card.Body>
          <Row className="filtros-row">
            <Col md={3}>
              <Form.Label>Grupo Empresa</Form.Label>
              <Select
                options={geOptions}
                value={geSelecionado}
                onChange={setGeSelecionado}
                placeholder="Selecione o GE..."
                styles={customStyles}
                components={{ DropdownIndicator }}
                isClearable
                noOptionsMessage={() => 'Nenhum GE encontrado'}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Empreendimento</Form.Label>
              <Select
                options={empreendimentoOptions}
                value={empreendimentoSelecionado}
                onChange={handleEmpreendimentoChange}
                placeholder="Buscar empreendimento..."
                styles={customStyles}
                components={{ DropdownIndicator }}
                isClearable
                noOptionsMessage={() => 'Nenhum empreendimento'}
              />
            </Col>
            <Col md={2}>
              <Form.Label>Imóvel</Form.Label>
              <Form.Control
                type="text"
                placeholder="Código"
                value={filtroImovel}
                onChange={e => setFiltroImovel(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label>Pré-Venda</Form.Label>
              <Form.Control
                type="text"
                placeholder="Número"
                value={filtroPreVenda}
                onChange={e => setFiltroPreVenda(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label>Corretor</Form.Label>
              <Form.Control
                type="text"
                placeholder="Código"
                value={filtroCorretor}
                onChange={e => setFiltroCorretor(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
        <div className="intencoes-action-row">
          <div className="status-group">
            <span className="status-label">Status:</span>
            {STATUS_OPTIONS.map(opt => (
              <Form.Check
                key={String(opt.value)}
                type="radio"
                id={`status-${opt.value}`}
                label={opt.label}
                checked={filtroStatus === opt.value}
                onChange={() => setFiltroStatus(opt.value)}
              />
            ))}
          </div>
          <div className="btn-group-actions">
            <Button
              size="sm"
              style={{ backgroundColor: '#7bb32e', borderColor: '#7bb32e' }}
              onClick={handleBuscar}
              disabled={!geSelecionado}
            >
              <FaSearch className="mr-1" /> Buscar
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={handleLimpar}>
              <FaEraser className="mr-1" /> Limpar
            </Button>
          </div>
        </div>
      </Card>

      {/* ===== GRID PRINCIPAL ===== */}
      <Card className="mb-3 shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Intenções de Vendas
            {intencoes.length > 0 && (
              <span className="header-badge-count">
                ({intencoes.length} {intencoes.length === 1 ? 'registro' : 'registros'})
              </span>
            )}
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-3"><Loading /></div>
          ) : intencoesOrdenadas.length === 0 ? (
            <div className="intencoes-empty-state">
              <FaInfoCircle className="empty-icon" />
              <div className="empty-text">
                {geSelecionado
                  ? 'Nenhum registro encontrado'
                  : 'Selecione um Grupo Empresa e clique em Buscar'}
              </div>
            </div>
          ) : (
            <>
              {/* Desktop */}
              <div className="intencoes-desktop">
                <div className="intencoes-grid-scroll">
                  <Table striped bordered hover size="sm" className="intencoes-vendas-table mb-0">
                    <thead>
                      <tr>
                        <th onClick={() => handleOrdenacao('codigo_ativo')}>
                          Código {iconeOrdenacao('codigo_ativo')}
                        </th>
                        <th onClick={() => handleOrdenacao('nome')}>
                          Descrição {iconeOrdenacao('nome')}
                        </th>
                        <th onClick={() => handleOrdenacao('quadra')}>
                          QD {iconeOrdenacao('quadra')}
                        </th>
                        <th onClick={() => handleOrdenacao('lote')}>
                          LT {iconeOrdenacao('lote')}
                        </th>
                        <th onClick={() => handleOrdenacao('status_imovel')}>
                          ST Imóvel {iconeOrdenacao('status_imovel')}
                        </th>
                        <th onClick={() => handleOrdenacao('nome_familia')}>
                          Empreendimento {iconeOrdenacao('nome_familia')}
                        </th>
                        <th onClick={() => handleOrdenacao('status_intencao')}>
                          ST Intenção {iconeOrdenacao('status_intencao')}
                        </th>
                        <th onClick={() => handleOrdenacao('pre_venda')}>
                          Pré-Venda {iconeOrdenacao('pre_venda')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {intencoesOrdenadas.map((item, idx) => (
                        <tr
                          key={idx}
                          onClick={() => setIntencaoSelecionada(item)}
                          className={
                            intencaoSelecionada &&
                            intencaoSelecionada.codigo_ativo === item.codigo_ativo
                              ? 'selecionada'
                              : ''
                          }
                        >
                          <td>{item.codigo_ativo}</td>
                          <td>{item.nome}</td>
                          <td>{item.quadra}</td>
                          <td>{item.lote}</td>
                          <td>{item.status_imovel}</td>
                          <td>{item.nome_familia}</td>
                          <td className="text-center">
                            {renderStatusBadge(item.status_intencao)}
                          </td>
                          <td>{item.pre_venda || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Mobile */}
              <div className="intencoes-mobile p-2">
                {intencoesOrdenadas.map((item, idx) => (
                  <div
                    key={idx}
                    className={`intencoes-mobile-card${
                      intencaoSelecionada &&
                      intencaoSelecionada.codigo_ativo === item.codigo_ativo
                        ? ' selecionada'
                        : ''
                    }`}
                    onClick={() => setIntencaoSelecionada(item)}
                  >
                    <div className="mobile-header">
                      <span className="mobile-codigo">
                        #{item.codigo_ativo} — {item.nome}
                      </span>
                      {renderStatusBadge(item.status_intencao, true)}
                    </div>
                    <div className="mobile-body">
                      <span>QD: {item.quadra || '-'}</span>
                      <span>LT: {item.lote || '-'}</span>
                      {item.pre_venda && <span>PV: {item.pre_venda}</span>}
                    </div>
                    <div className="mobile-body">
                      {item.nome_familia}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ===== PRESTADORES ===== */}
      {intencaoSelecionada && (
        <Card className="mb-3 shadow-sm card-appear">
          <Card.Header
            className="d-flex justify-content-between align-items-center py-2"
            style={{ backgroundColor: '#0747a6', color: '#fff' }}
          >
            <strong>
              Prestadores — Imóvel {intencaoSelecionada.codigo_ativo}
              {intencaoSelecionada.pre_venda
                ? ` | PV: ${intencaoSelecionada.pre_venda}`
                : ''}
            </strong>
          </Card.Header>
          <Card.Body className="p-0">
            {loadingPrestadores ? (
              <div className="p-3"><Loading /></div>
            ) : !intencaoSelecionada.pre_venda ? (
              <div className="intencoes-empty-state">
                <FaInfoCircle className="empty-icon" />
                <div className="empty-text">
                  Intenção sem pré-venda — prestadores não disponíveis.
                </div>
              </div>
            ) : prestadores.length === 0 ? (
              <div className="intencoes-empty-state">
                <FaInfoCircle className="empty-icon" />
                <div className="empty-text">Nenhum prestador encontrado</div>
              </div>
            ) : (
              <>
                {/* Desktop */}
                <div className="intencoes-desktop">
                  <div className="prestadores-scroll">
                    <Table bordered size="sm" className="prestadores-table mb-0">
                      <thead>
                        <tr>
                          <th>Conta</th>
                          <th>Descrição</th>
                          <th>Tipo</th>
                          <th>Sigla</th>
                          <th>Fator</th>
                          <th style={{ width: 80 }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prestadores.map((p, idx) => {
                          const bgColor = intToHexColor(p.cor);
                          return (
                            <tr
                              key={idx}
                              style={bgColor
                                ? { borderLeft: `4px solid ${bgColor}` }
                                : {}
                              }
                            >
                              <td>{p.codigo_conta}</td>
                              <td>{p.apelido_conta}</td>
                              <td>{p.codigo_tipo}</td>
                              <td>{p.sigla}</td>
                              <td>{formatarMonetario(p.fator)}</td>
                              <td className="text-center">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="btn-detalhar"
                                  onClick={() => handleDetalhar(p)}
                                  title="Detalhar recebimento"
                                >
                                  <FaEye />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </div>

                {/* Mobile */}
                <div className="intencoes-mobile p-2">
                  {prestadores.map((p, idx) => {
                    const bgColor = intToHexColor(p.cor);
                    return (
                      <div
                        key={idx}
                        className="prestador-mobile-card"
                        style={bgColor
                          ? { borderLeft: `4px solid ${bgColor}` }
                          : {}
                        }
                      >
                        <div className="prestador-mobile-header">
                          <div className="prestador-mobile-info">
                            <strong>{p.codigo_conta}</strong>
                            {p.apelido_conta}
                          </div>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleDetalhar(p)}
                          >
                            <FaEye className="mr-1" /> Detalhar
                          </Button>
                        </div>
                        <div className="prestador-mobile-meta">
                          <span>Tipo: {p.sigla}</span>
                          <span>Fator: {formatarMonetario(p.fator)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {textoComissao.length > 0 && (
                  <div className="texto-comissao">
                    {textoComissao.map((linha, idx) => (
                      <div key={idx}>{linha}</div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ===== MODAL DETALHAMENTO ===== */}
      <ModalDetalhamento
        show={showModal}
        onHide={() => setShowModal(false)}
        detalhamento={detalhamento}
        prestador={prestadorDetalhe}
        intencao={intencaoSelecionada}
      />
    </Container>
  );
}
